import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { Button, Card, LinkButton, Notice, Switch, TextField, buttonClass } from './ui.js';

/**
 * Site settings (site-settings.html): the public status page (T-70) on or off, its address, name
 * and a note, the logo by plan; open it or copy its link to share.
 */
export function StatusPageCard({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const key = ['site', siteId, 'status-page'];
  const settings = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET('/sites/{siteId}/status-page', { params: { path: { siteId } } })),
  });
  const [enabled, setEnabled] = useState(false);
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const noteId = useId();
  useEffect(() => {
    if (!settings.data) return;
    setEnabled(settings.data.enabled);
    setSlug(settings.data.slug);
    setTitle(settings.data.title ?? '');
    setMessage(settings.data.message ?? '');
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT('/sites/{siteId}/status-page', {
          params: { path: { siteId } },
          body: {
            enabled,
            slug: slug.trim().toLowerCase(),
            title: title.trim() || null,
            message: message.trim() || null,
          },
        }),
      ),
    onSuccess: (data) => client.setQueryData(key, data),
  });
  if (!settings.data) return null;
  // The saved address is the one that works; the field shows the one being typed.
  const url = `${window.location.origin}${settings.data.path}`;
  const typed = `${window.location.origin}/status/${slug.trim().toLowerCase()}`;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <Card>
      <h2>
        {t('statusPage.title')} <small>{t('statusPage.subtitle')}</small>
      </h2>
      <form onSubmit={submit}>
        <Switch label={t('statusPage.enabled')} checked={enabled} onChange={setEnabled} />
        <div className="fields">
          <TextField
            label={t('statusPage.slug')}
            hint={t('statusPage.slugHint', { url: typed })}
            value={slug}
            maxLength={40}
            // Browsers read the pattern with the v flag: a hyphen in a class must be escaped.
            pattern="[a-z0-9][a-z0-9\-]{1,38}[a-z0-9]"
            onChange={(e) => setSlug(e.target.value)}
          />
          <TextField
            label={t('statusPage.name')}
            hint={t('statusPage.nameHint')}
            optional
            maxLength={80}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        {/* Several lines, as the status page shows them (review #187); the sheet has one. */}
        <div className="field">
          <label htmlFor={noteId}>
            {t('statusPage.message')} ({t('app.optional')})
          </label>
          <textarea
            id={noteId}
            rows={2}
            maxLength={500}
            value={message}
            aria-describedby={`${noteId}-hint`}
            onChange={(e) => setMessage(e.target.value)}
          />
          <p id={`${noteId}-hint`} className="hint">
            {t('statusPage.messageHint')}
          </p>
        </div>
        {/* The logo of the report (T-66) shows on the page from Business on. */}
        {settings.data.logo ? (
          <p className="bodyM">{t('statusPage.logo')}</p>
        ) : (
          <div className="upsell">
            <span>{t('statusPage.logoPlan')}</span>
            <LinkButton to="/billing" variant="secondary" size="sm">
              {t('settings.plans')}
            </LinkButton>
          </div>
        )}
        {save.isError ? <Notice kind="error">{errorMessage(save.error, t)}</Notice> : null}
        {save.isSuccess ? <Notice kind="success">{t('app.saved')}</Notice> : null}
        <div className="acts">
          <Button type="submit" size="sm" busy={save.isPending}>
            {t('app.save')}
          </Button>
          {settings.data.enabled ? (
            <>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className={buttonClass('secondary', 'sm')}
              >
                {t('statusPage.open')}
              </a>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Refused copying changes nothing: the address stands under its field.
                  void navigator.clipboard?.writeText(url).then(
                    () => setCopied(true),
                    () => undefined,
                  );
                }}
              >
                {copied ? t('statusPage.shared') : t('statusPage.share')}
              </Button>
            </>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
